// Quick guide — Regular verbs, present tense. Explains the mechanic (chop the
// infinitive's last two letters → add the ending), recreates the classic
// "conjugation endings" 3-grid board as a hand-drawn SVG, then shows the three
// families fully conjugated. Hand-drawn paper style.
import { speakButtonHTML } from "../lib/audio.js";

const PRONOUNS = ["io", "tu", "lui / lei", "noi", "voi", "loro"];
const VERBS = [
  { inf: "parlare", type: "-are", en: "to speak", stem: "parl", endings: ["o", "i", "a", "iamo", "ate", "ano"], color: "text-rose-600" },
  { inf: "credere", type: "-ere", en: "to believe", stem: "cred", endings: ["o", "i", "e", "iamo", "ete", "ono"], color: "text-blue-700" },
  { inf: "dormire", type: "-ire", en: "to sleep", stem: "dorm", endings: ["o", "i", "e", "iamo", "ite", "ono"], color: "text-teal-600" },
];

// One labelled 2-column × 3-row endings grid (singular | plural), hand-drawn.
function grid(x, color, label, sing, plur) {
  const cw = 80, ch = 50, gy = 42;
  const c = (cx, ry, t) => `<text x="${cx}" y="${gy + ch * ry + 33}" text-anchor="middle">${t}</text>`;
  return `
    <text x="${x + cw}" y="${gy - 12}" text-anchor="middle" font-family="Patrick Hand,sans-serif" font-size="18" fill="#475569">${label} verbs</text>
    <g filter="url(#rv-rough)" stroke="#3f3f46" stroke-width="2" fill="none">
      <rect x="${x}" y="${gy}" width="${cw * 2}" height="${ch * 3}" rx="3"></rect>
      <line x1="${x + cw}" y1="${gy}" x2="${x + cw}" y2="${gy + ch * 3}"></line>
      <line x1="${x}" y1="${gy + ch}" x2="${x + cw * 2}" y2="${gy + ch}"></line>
      <line x1="${x}" y1="${gy + ch * 2}" x2="${x + cw * 2}" y2="${gy + ch * 2}"></line>
    </g>
    <g font-family="Patrick Hand,sans-serif" font-weight="700" fill="${color}" font-size="22">
      ${c(x + cw / 2, 0, sing[0])}${c(x + cw / 2, 1, sing[1])}${c(x + cw / 2, 2, sing[2])}
      ${c(x + cw * 1.5, 0, plur[0])}${c(x + cw * 1.5, 1, plur[1])}${c(x + cw * 1.5, 2, plur[2])}
    </g>`;
}

const ENDINGS_FIGURE = `
  <svg viewBox="0 0 640 210" class="w-full h-auto max-w-2xl mx-auto" role="img" aria-label="Present-tense endings for -are, -ere and -ire verbs">
    <defs><filter id="rv-rough"><feTurbulence type="fractalNoise" baseFrequency="0.014" numOctaves="2" seed="4" result="n"></feTurbulence><feDisplacementMap in="SourceGraphic" in2="n" scale="3"></feDisplacementMap></filter></defs>
    ${grid(20, "#be185d", "-are", ["o", "i", "a"], ["iamo", "ate", "ano"])}
    ${grid(240, "#1d4ed8", "-ere", ["o", "i", "e"], ["iamo", "ete", "ono"])}
    ${grid(460, "#0d9488", "-ire", ["o", "i", "e"], ["iamo", "ite", "ono"])}
  </svg>`;

function colHTML(v) {
  const rows = v.endings
    .map(
      (e, i) => `
      <div class="flex items-baseline justify-between gap-2 py-0.5 border-b border-dashed border-amber-300/40 last:border-0">
        <span class="text-sage-400 text-sm" style="font-family:Inter,sans-serif">${PRONOUNS[i]}</span>
        <span><span class="text-sage-700">${v.stem}</span><span class="font-bold text-rose-500">${e}</span>${speakButtonHTML(v.stem + e)}</span>
      </div>`
    )
    .join("");
  return `
    <div class="flex-1 min-w-[8.5rem]">
      <h3 class="${v.color} text-2xl sketch-underline inline-block leading-none">${v.inf}</h3>
      <p class="text-xs text-sage-500 mb-2 mt-1" style="font-family:Inter,sans-serif">${v.type} · ${v.en}</p>
      ${rows}
    </div>`;
}

export function regularVerbsView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Regular Verbs · Present 🔁</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Almost every Italian verb ends in <b>-are</b>, <b>-ere</b> or <b>-ire</b>. The pattern is always the same.</p>
      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg">
        <h3 class="text-2xl text-sage-700 sketch-underline inline-block mb-2">How to conjugate</h3>
        <ol class="list-decimal pl-6 space-y-1 text-sage-700 mb-2">
          <li>Take the infinitive and <b>chop off the ending</b> (the <b>-are / -ere / -ire</b>) to get the <b>stem</b>.</li>
          <li>Add the ending for the person you mean.</li>
        </ol>
        <p class="text-sage-700 mb-5 text-xl">parl<span class="text-sage-300">are</span> → <b>parl</b> → parl<span class="text-rose-500 font-bold">o</span>, parl<span class="text-rose-500 font-bold">i</span>, parl<span class="text-rose-500 font-bold">a</span>…</p>

        <h3 class="text-2xl text-sage-700 sketch-underline inline-block mb-2">The endings</h3>
        ${ENDINGS_FIGURE}

        <h3 class="text-2xl text-sage-700 sketch-underline inline-block mt-6 mb-2">Worked out in full</h3>
        <div class="flex flex-wrap gap-5">${VERBS.map(colHTML).join("")}</div>
      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Notice: <b>-ere</b> and <b>-ire</b> are identical except for the <i>voi</i> form (credete vs dormite). Many -ire verbs also follow the <b>-isco</b> pattern (finire → finisco).</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
